import { sessionStorage as safeSessionStorage } from 'utils/browserStorage';
type CreateRequestScope = 'job-post' | 'location-add' | 'manual-shift';

const CREATE_STORAGE_KEY_PREFIX = 'ts_create_idempotency_v1:';
const LEGACY_JOB_POST_KEY_PREFIX = 'ts_job_post_idempotency_v1:';

type StoredCreateRequestKey = {
    key: string;
};

const canonicalize = (value: unknown): unknown => {
    if (value === null || typeof value !== 'object') return value;
    if (Array.isArray(value)) return value.map(canonicalize);

    return Object.keys(value as Record<string, unknown>)
        .sort()
        .reduce<Record<string, unknown>>((result, key) => {
            const normalized = canonicalize((value as Record<string, unknown>)[key]);
            if (typeof normalized !== 'undefined') result[key] = normalized;
            return result;
        }, {});
};

const hashFingerprint = (value: string): string => {
    // Two independent 32-bit hashes plus length keep the storage key compact
    // without persisting potentially sensitive shift details in sessionStorage.
    let firstHash = 2166136261;
    let secondHash = 5381;
    for (let index = 0; index < value.length; index += 1) {
        const character = value.charCodeAt(index);
        firstHash ^= character;
        firstHash = Math.imul(firstHash, 16777619);
        secondHash = Math.imul(secondHash, 33) ^ character;
    }
    return [
        value.length.toString(36),
        (firstHash >>> 0).toString(36),
        (secondHash >>> 0).toString(36),
    ].join('-');
};

const randomCreateKey = (scope: CreateRequestScope): string => {
    try {
        // Older Cordova WebViews do not type or expose randomUUID.
        const generated = (window.crypto as Crypto & { randomUUID?: () => string })?.randomUUID?.();
        if (generated) return `${scope}:${generated}`;
    } catch {}

    const randomPart = Math.random().toString(36).slice(2, 12);
    return `${scope}:${Date.now().toString(36)}:${randomPart}`;
};

const storageKeyFor = (scope: CreateRequestScope, fingerprint: string) => {
    const hashedFingerprint = hashFingerprint(fingerprint);
    return scope === 'job-post'
        ? `${LEGACY_JOB_POST_KEY_PREFIX}${hashedFingerprint}`
        : `${CREATE_STORAGE_KEY_PREFIX}${scope}:${hashedFingerprint}`;
};

export const getOrCreateCreateRequestKey = (
    scope: CreateRequestScope,
    payload: object,
): string => {
    const fingerprint = JSON.stringify(canonicalize(payload));
    const storageKey = storageKeyFor(scope, fingerprint);
    try {
        const raw = safeSessionStorage.getItem(storageKey);
        if (raw) {
            const stored = JSON.parse(raw) as StoredCreateRequestKey;
            if (stored.key) return stored.key;
        }
    } catch {}

    const key = randomCreateKey(scope);
    try {
        safeSessionStorage.setItem(storageKey, JSON.stringify({ key }));
    } catch {}
    return key;
};

export const clearCreateRequestKey = (
    scope: CreateRequestScope,
    payload: object,
): void => {
    const fingerprint = JSON.stringify(canonicalize(payload));
    try {
        safeSessionStorage.removeItem(storageKeyFor(scope, fingerprint));
    } catch {}
};

export const getOrCreateJobPostRequestKey = (payload: object): string =>
    getOrCreateCreateRequestKey('job-post', payload);

export const clearJobPostRequestKey = (payload: object): void =>
    clearCreateRequestKey('job-post', payload);
